import { Link } from 'react-router-dom';

/**
 * Static step-by-step guide: how to run a time-series analysis, how to
 * generate a dissertation from it, and how to submit an assignment.
 * Matches the order users encounter in the app and links to the exact
 * pages they need next.
 */
export default function Help() {
  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4 text-center text-white">
          <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
            <i className="fas fa-question-circle me-1"></i>Help
          </span>
          <h1 style={{ fontWeight: 800, fontSize: '2.6rem' }}>How it works</h1>
          <p className="lead mx-auto" style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 720 }}>
            Step-by-step guides for running a time-series analysis, generating a dissertation,
            and producing an assignment.
          </p>
        </div>
      </section>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            {/* Table of contents */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fas fa-list me-2" style={{ color: 'var(--primary)' }}></i>
                  On this page
                </h5>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <a href="#before-you-start" className="text-decoration-none">1. Before you start — prerequisites</a>
                  </li>
                  <li className="mb-2">
                    <a href="#timeseries" className="text-decoration-none">2. How to run a time-series analysis</a>
                  </li>
                  <li className="mb-2">
                    <a href="#dissertation" className="text-decoration-none">3. How to generate a dissertation</a>
                  </li>
                  <li className="mb-2">
                    <a href="#assignment" className="text-decoration-none">4. How to produce an assignment</a>
                  </li>
                  <li className="mb-2">
                    <a href="#downloads" className="text-decoration-none">5. Downloads (PDF, DOCX, CSV)</a>
                  </li>
                  <li className="mb-2">
                    <a href="#credits" className="text-decoration-none">6. Credits & billing</a>
                  </li>
                  <li>
                    <a href="#troubleshooting" className="text-decoration-none">7. Troubleshooting & FAQ</a>
                  </li>
                </ul>
              </div>
            </div>

            {/* 1. Before you start */}
            <Section id="before-you-start" title="1. Before you start — prerequisites" icon="fa-check-double">
              <ol>
                <li>
                  <strong>Create an account.</strong> Sign up from <Link to="/register">the
                  register page</Link> with email &amp; password, or use
                  <em> Continue with Google</em> to skip password management entirely.
                  New accounts start with <strong>0 credits</strong>.
                </li>
                <li>
                  <strong>Buy credits.</strong> From <Link to="/pricing">Pricing</Link> pick a
                  plan — 1 credit for a single assignment, 31 credits for a full
                  dissertation, or a bundle. Payment goes through PawaPay
                  mobile-money or card.
                </li>
                <li>
                  <strong>Know what you want to investigate.</strong> For a dissertation, you
                  need a country + two World Bank indicators (e.g. GDP growth vs
                  Trade % of GDP). For an assignment, you need a topic, the
                  instructions from your lecturer, and an academic level.
                </li>
              </ol>
            </Section>

            {/* 2. Time-Series */}
            <Section id="timeseries" title="2. How to run a time-series analysis" icon="fa-chart-line">
              <p className="text-muted small mb-3">
                A time-series analysis produces five research-topic suggestions and
                a full Chapter-4-style econometrics report. <strong>It's a prerequisite
                for generating a dissertation</strong> — the dissertation's topic is
                picked from the research questions it produces. Cost: 10 credits.
              </p>
              <ol>
                <li>
                  Go to <Link to="/worldbank/new">Time Series → New analysis</Link>.
                </li>
                <li>
                  <strong>Pick two indicators.</strong> Variable 1 and Variable 2 must be
                  different. Common pairs: GDP (current US$) vs Trade (% of GDP),
                  Inflation vs Lending rate, CO₂ emissions vs GDP per capita.
                </li>
                <li>
                  <strong>Pick a country.</strong> Zambia is the default; switch to any
                  country in the World Bank catalogue.
                </li>
                <li>
                  <strong>Set a year window.</strong> 40 years is the default; shorter
                  windows give less statistical power but more recent relevance.
                </li>
                <li>
                  <strong>Click <em>Analyze</em></strong>. The server runs the full
                  econometric pipeline — descriptive stats, ADF/KPSS/Phillips-Perron
                  unit root tests, Zivot-Andrews with structural breaks, Engle-Granger
                  + Johansen cointegration, VAR + VECM (when cointegrated),
                  Granger + Toda-Yamamoto causality, IRF + FEVD with bootstrap
                  confidence bands, CUSUM stability, Chow break at the ZA date,
                  rolling correlation, and out-of-sample forecast evaluation.
                </li>
                <li>
                  <strong>Wait for completion</strong> (usually 2–8 minutes). You can close
                  the page — the analysis runs in the background.
                </li>
                <li>
                  <strong>Review the results</strong>. You'll see charts (line, bar, scatter,
                  rolling correlation), every stat table, and a{' '}
                  <em>Raw Values from World Bank</em> section at the bottom showing
                  the untransformed inputs.
                </li>
              </ol>
              <div className="alert alert-info small mb-0">
                <i className="fas fa-info-circle me-2"></i>
                The analysis also generates <strong>five research topics</strong> — these
                become the picker you'll see on the dissertation Generate page.
              </div>
            </Section>

            {/* 3. Dissertation */}
            <Section id="dissertation" title="3. How to generate a dissertation" icon="fa-graduation-cap">
              <p className="text-muted small mb-3">
                Cost: 31 credits. Duration: 1–3 hours (runs in the background;
                you can close the tab). Produces a complete 5-chapter
                dissertation with references.
              </p>
              <ol>
                <li>
                  <strong>Run a time-series analysis first</strong> (step 2 above). Without
                  one, you'll see an empty-state nudge sending you there.
                </li>
                <li>
                  Go to <Link to="/generate">Generate</Link>.
                </li>
                <li>
                  <strong>Pick a research topic</strong> from the dropdown — one of the five
                  topics from any completed time-series analysis. The analysis's
                  dataset and statistics are automatically attached to Chapter 4.
                </li>
                <li>
                  <strong>Choose a language</strong> (English is the default; French,
                  Spanish, Portuguese, Swahili, Arabic, Chinese are supported).
                </li>
                <li>
                  Click <strong>Generate (31 credits)</strong>. You'll land on a live-progress
                  page showing phase-by-phase progress, word count, and chapter
                  status.
                </li>
                <li>
                  <strong>Leave it running</strong>. The process is resilient — if a server
                  instance is evicted mid-run, another picks it up within ~3 min
                  from the last completed phase.
                </li>
                <li>
                  When complete, click <strong>Open your dissertation</strong>. You'll see a
                  table of contents, all 5 chapters, figures + tables, and a
                  web-search-validated APA References list.
                </li>
              </ol>
              <div className="alert alert-warning small mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                If generation fails mid-run, a <strong>Resume from checkpoint</strong> button
                appears on the live page — clicking it picks up from the last
                completed phase instead of restarting from Chapter 1.
              </div>
            </Section>

            {/* 4. Assignment */}
            <Section id="assignment" title="4. How to produce an assignment" icon="fa-file-alt">
              <p className="text-muted small mb-3">
                Cost: 1 credit. Duration: 3–10 minutes. Produces a complete
                essay / report / case-study with in-text citations and a
                web-search-validated References list.
              </p>
              <ol>
                <li>
                  Go to <Link to="/assignments">Assignments → New</Link>.
                </li>
                <li>
                  <strong>Assignment type</strong>: essay, business report, case study,
                  literature review, reflective essay, lab report, etc.
                </li>
                <li>
                  <strong>Topic / question</strong>: paste the exact question from your
                  lecturer. The more detail, the better — include any scenario
                  text, required frameworks, or specific figures to engage with.
                </li>
                <li>
                  <strong>Word count</strong>: 1000–8000. The generator will keep extending
                  until it hits the target.
                </li>
                <li>
                  <strong>Academic level</strong>: undergrad, postgrad, PhD.
                </li>
                <li>
                  <strong>Referencing style</strong>: APA, Harvard, MLA, Chicago, OSCOLA, or
                  Bluebook.
                </li>
                <li>
                  Click <strong>Generate</strong>. The live page shows phase progress and a
                  preview of the content as it comes in.
                </li>
                <li>
                  When complete, review the output. The final References section
                  is <strong>verified against live web sources by the AI</strong> — entries
                  marked <em>unverified</em> should be replaced before submission.
                </li>
              </ol>
            </Section>

            {/* 5. Downloads */}
            <Section id="downloads" title="5. Downloads (PDF, DOCX, CSV)" icon="fa-download">
              <p className="text-muted small mb-3">
                Every result page has Download buttons. Click them and the file
                saves directly — no re-authentication required.
              </p>
              <ul>
                <li><strong>Dissertation / Assignment</strong>: PDF and DOCX (Microsoft Word).
                Both contain the full body, references, and any embedded figures.</li>
                <li><strong>Time-series analysis</strong>: PDF, DOCX, and CSV. CSV contains
                only the raw year-by-year World Bank values for spreadsheet use
                (Excel, Google Sheets).</li>
              </ul>
              <div className="alert alert-info small mb-0">
                <i className="fas fa-info-circle me-2"></i>
                DOCX files save with a <code>.docx</code> extension automatically. If Windows'
                Save-As dialog hides the extension, click Save — the file will
                still open in Word.
              </div>
            </Section>

            {/* 6. Credits */}
            <Section id="credits" title="6. Credits & billing" icon="fa-coins">
              <table className="table table-sm">
                <thead>
                  <tr><th>Product</th><th>Credits</th><th>Pricing plan</th></tr>
                </thead>
                <tbody>
                  <tr><td>One assignment</td><td>1</td><td>Assignment (K82)</td></tr>
                  <tr><td>5 assignments</td><td>5</td><td>Assignment 5-Pack (K410)</td></tr>
                  <tr><td>One time-series analysis</td><td>10</td><td>included in any pack</td></tr>
                  <tr><td>One full dissertation</td><td>31</td><td>Dissertation (K2,500)</td></tr>
                  <tr><td>3 dissertations or 93 assignments</td><td>93</td><td>Standard (K7,000)</td></tr>
                  <tr><td>7 dissertations or 217 assignments</td><td>217</td><td>Premium (K15,000)</td></tr>
                </tbody>
              </table>
              <p className="small text-muted mb-0">
                Credits never expire. Failed generations refund automatically. See{' '}
                <Link to="/pricing">Pricing</Link> for the full table.
              </p>
            </Section>

            {/* 7. Troubleshooting */}
            <Section id="troubleshooting" title="7. Troubleshooting & FAQ" icon="fa-life-ring">
              <h6 className="fw-semibold mt-3">I closed the tab — did my dissertation stop?</h6>
              <p className="small text-muted">
                No. Generation runs server-side. Reopen the live page or go to{' '}
                <Link to="/dashboard">Dashboard</Link> to watch progress.
              </p>

              <h6 className="fw-semibold mt-3">My generation failed — what now?</h6>
              <p className="small text-muted">
                A <em>Resume from checkpoint</em> button appears on the live page. It
                picks up from the last completed phase. Credits are NOT
                re-deducted on resume.
              </p>

              <h6 className="fw-semibold mt-3">The queue says I'm in position #7 — when will it start?</h6>
              <p className="small text-muted">
                Each worker handles up to 5 jobs in parallel and the system
                auto-scales up to 10 workers. At that ceiling you'll typically
                wait 15–30 min per position ahead of you.
              </p>

              <h6 className="fw-semibold mt-3">Google sign-in says "unauthorized domain"</h6>
              <p className="small text-muted">
                Your admin needs to whitelist the hosting domain in Firebase
                Authentication → Settings → Authorized domains.
              </p>

              <h6 className="fw-semibold mt-3">A reference is marked "unverified" — what do I do?</h6>
              <p className="small text-muted">
                The AI couldn't confirm that source exists on the live web. Replace
                it manually with a real paper, or accept the suggested
                replacement from the validator's note.
              </p>

              <h6 className="fw-semibold mt-3">I need more help</h6>
              <p className="small text-muted mb-0">
                Email <a href="mailto:support@dissertationisland.com">support@dissertationisland.com</a>
                {' '}with your dissertation/assignment ID and a screenshot, and we'll dig in.
              </p>
            </Section>

            <div className="text-center mt-5 mb-4">
              <Link to="/dashboard" className="btn btn-cta btn-lg me-2">
                <i className="fas fa-home me-1"></i>Back to dashboard
              </Link>
              <Link to="/generate" className="btn btn-outline-primary btn-lg">
                <i className="fas fa-rocket me-1"></i>Start generating
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({ id, title, icon, children }) {
  return (
    <div id={id} className="card mb-4">
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          color: '#fff',
          padding: '0.9rem 1.2rem',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <h4 className="mb-0" style={{ fontWeight: 700, fontSize: '1.25rem' }}>
          <i className={`fas ${icon} me-2`}></i>{title}
        </h4>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}
